<template>
  <div ref="menuContainer">
    <button
      id="menu-1"
      class="bg-white rounded-full flex text-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary"
      aria-expanded="false"
      aria-haspopup="true"
      @click="isOpen = !isOpen"
    >
      <span class="sr-only">
        Ouvrir le menu utilisateur
      </span>

      <div class="button-top">
        <Icon name="mdi-account-circle" :size="22" class="text-primary" />
      </div>
    </button>

    <div
      v-show="isOpen"
      class="origin-top-right absolute z-50 right-0 mt-2 w-60 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none"
      role="menu"
      aria-orientation="vertical"
      aria-labelledby="menu-1"
    >
      <div
        v-if="experience"
        class="flex gradient justify-between items-end h-24 cursor-pointer hover:opacity-95"
        @click="() => router.push({name: 'experience'})"
      >
        <div class="flex flex-col pl-4 pb-4">
          <p class="text-white text-sm">
            {{ experience.tiers.current.name }}
          </p>
          <p class="text-white text-xs font-semibold">
            Voir mes avantages >
          </p>
        </div>
        <img :src="experience.tiers.current.full_image_path" class="h-20" alt="Pharmacie">
      </div>
      <div v-else class="flex gradient justify-center">
        <img src="@/assets/images/avatar-my-profile.png" alt="Pharmacie">
      </div>
      <div
        v-if="environment"
        class="py-1"
      >
        <p class="block px-4 pt-2 text-sm text-primary font-semibold">
          {{ environment.entity.name }}
        </p>
        <p v-if="environment?.user?.email" class="block px-4 pb-2 text-xs text-primary">
          {{ environment.user.email }}
        </p>
      </div>
      <div>
        <router-link
          class="button-user-menu block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100"
          :to="{ name: 'healthcenter' }"
          @click="closeMenu"
        >
          Ma pharmacie
        </router-link>
        <router-link
          class="button-user-menu block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100"
          :to="{ name: 'team' }"
          @click="closeMenu"
        >
          Mon équipe
        </router-link>
      </div>
      <div>
        <router-link
          class="button-user-menu block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100"
          :to="{ name: 'billing' }"
          @click="closeMenu"
        >
          Mes factures
        </router-link>
      </div>
      <div>
        <router-link
          class="button-user-menu block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100"
          :to="{ name: 'notification.preference' }"
          @click="closeMenu"
        >
          Paramètres de notification
        </router-link>
      </div>
      <div>
        <router-link
          class="button-user-menu block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100"
          :to="{ name: 'support' }"
          @click="closeMenu"
        >
          Support
        </router-link>
      </div>
      <div>
        <router-link
          class="button-user-menu block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100"
          :to="{ name: 'sponsorship' }"
          @click="closeMenu"
        >
          Parrainage
        </router-link>
      </div>
      <div>
        <router-link
          class="button-user-menu block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100"
          :to="{ name: 'cgu' }"
          @click="closeMenu"
        >
          CGU
        </router-link>
      </div>
      <div>
        <router-link
          class="button-user-menu block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100"
          :to="{ name: 'privacy-policy' }"
          @click="closeMenu"
        >
          Politique de confidentialité des données
        </router-link>
      </div>
      <div v-if="environment?.user.is_superadmin">
        <router-link
          class="button-user-menu block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100"
          :to="{ name: 'login.as' }"
          @click="closeMenu"
        >
          Se connecter en tant que ...
        </router-link>
      </div>
      <div>
        <div
          class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 focus:bg-gray-100 cursor-pointer"
          @click="logout()"
        >
          Déconnexion
        </div>
      </div>
      <div class="px-4 py-2">
        <div v-if="lastSyncDate" class="flex items-center gap-2 pb-2">
          <div class="size-3 shrink-0 rounded-full" :class="isSyncStale ? 'bg-orange' : 'bg-secondary'" />
          <label
            class="text-xs text-gray-700"
          >
            Dernière synchronisation le {{ lastSyncDate }}
          </label>
        </div>
        <div class="flex flex-wrap gap-1.5">
          <Monitor :size="14" />
          <label v-if="host?.name" class="text-xs text-gray-700">
            {{ host.name }}
          </label>
        </div>
        <label
          class="block text-xs text-gray-700"
        >
          {{ `Version ${appVersion} - ${env}` }}
        </label>
        <label
          class="block pt-2 text-xs text-gray-700"
        >
          Fait avec ❤️ à Tours et Paris
        </label>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {useSystemStore} from '@/stores/system.store';
import {computed, ref, watch} from 'vue';
import {useRouter} from 'vue-router';
import {storeToRefs} from 'pinia';
import {version} from '../../../../package.json';
import Icon from "@/core/components/icon/Icon.vue";
import {useExperienceStore} from "@/stores/experience.store";
import {Monitor} from "@lucide/vue";
import useLgoSyncState from "@/core/composables/sante-secure/useLgoSyncState";

const router = useRouter();
const systemStore = useSystemStore();
const {isSyncStale, lastSyncDate} = useLgoSyncState();
const {environment, host} = storeToRefs(systemStore);
const {experience} = storeToRefs(useExperienceStore());

const menuContainer = ref<null | HTMLElement>(null);
const isOpen = ref(false);

const env = computed(() => {
    switch (process.env.NODE_ENV) {
        case 'development':
            return 'dev';
        case 'production':
            return 'prod';
        case 'staging':
            return 'stag';
        default:
            return '???';
    }
});

const appVersion = computed(() => {
    return version;
});

const handleClickOutside = (event) => {
    if (menuContainer.value && !menuContainer.value.contains(event.target)) {
        isOpen.value = false;
    }
};

const closeMenu = () => {
    isOpen.value = false;
};

const logout = () => {
    closeMenu();
    router.push({name: 'logout'});
};

watch(() => isOpen.value, (value) => {
    if (value) {
        document.addEventListener('click', handleClickOutside);
    } else {
        document.removeEventListener('click', handleClickOutside);
    }
});
</script>
